import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, PhoneCall, ShieldCheck, Clock, MapPin, Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";
import {
  SectionHeading, ServiceCard, FeatureCard, StatPill
} from "../components/sections/Shared";
import {
  HERO, SERVICES, WHY_US, TESTIMONIALS, PARTNERS, COVERAGE, PROCESS_STEPS
} from "../data/mock";
import { HOME_SEO, SEO_SERVICES } from "../data/seo";

export default function Home() {
  return (
    <>
      <Seo title={HOME_SEO.title} description={HOME_SEO.description} path="/" />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-orange-50/40">
        <div className="speed-line" style={{ top: "22%", width: "30%" }} />
        <div className="speed-line" style={{ top: "68%", width: "22%", animationDelay: "1.2s" }} />

        <div className="container-518 relative pt-14 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs font-semibold text-slate-700 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-[#f59321] animate-pulse" />
              {HERO.eyebrow}
            </div>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl xl:text-6xl font-extrabold text-[#0f2847] leading-[1.05]">
              Reliable Local &amp; Interstate{" "}
              <span className="text-[#f59321]">Courier Services</span>{" "}
              in New York's Capital Region
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              {HOME_SEO.paragraph}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/get-a-quote">
                <Button className="btn-lift bg-[#f59321] hover:bg-[#e07f10] text-white font-semibold rounded-full h-14 px-7 text-base shadow-lg">
                  Request a Quote <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:5189544701">
                <Button variant="outline" className="bg-white border-2 border-[#0f2847] text-[#0f2847] hover:bg-[#0f2847] hover:text-white rounded-full h-14 px-7 text-base font-semibold">
                  <PhoneCall className="w-4 h-4 mr-2" />
                  Call or text 518-954-4701
                </Button>
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-slate-200 pt-8">
              {HERO.stats.map((s, i) => (
                <StatPill key={i} value={s.value} label={s.label} />
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 relative animate-fade-up">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-200">
              <img src={HERO.image} alt="518 Courier same-day delivery van in Albany, NY" className="w-full h-[420px] md:h-[520px] object-cover" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f2847]/60 via-transparent to-transparent" />
              <div className="absolute top-5 left-5 flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-md">
                <span className="relative flex w-2.5 h-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <span className="text-xs font-bold text-[#0f2847]">Dispatch is live now</span>
              </div>
              <div className="absolute bottom-5 left-5 right-5 md:right-auto md:max-w-xs bg-white/95 backdrop-blur rounded-2xl p-4 shadow-xl border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#f59321]/15 text-[#f59321] flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Sample order · illustration</div>
                    <div className="text-sm font-bold text-[#0f2847]">Pickup → Drop-off · live tracking</div>
                  </div>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full w-3/4 bg-[#f59321] rounded-full" />
                </div>
              </div>
            </div>
            <div className="hidden md:flex absolute -top-6 -right-4 bg-white shadow-xl rounded-2xl p-4 border border-slate-100 items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#0f2847] text-white flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-[#0f2847] text-sm">Locally operated</div>
                <div className="text-xs text-slate-500">Based in Schenectady, NY</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES — SEO-linked */}
      <section className="py-20 md:py-28 bg-slate-50" id="services">
        <div className="container-518">
          <SectionHeading eyebrow="What we deliver" title="Courier services built for the" accent="518 & beyond." description="From a single envelope across downtown Albany to interstate runs into Massachusetts, Connecticut and Vermont — we move it on time, every time." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {SEO_SERVICES.map((s) => (
              <Link key={s.slug} to={`/${s.slug}`} className="card-lift group bg-white rounded-2xl border border-slate-200 p-6 md:p-7 flex flex-col h-full">
                <div className="w-14 h-14 rounded-2xl bg-[#0f2847] text-white flex items-center justify-center mb-5 group-hover:bg-[#f59321] transition-colors font-display font-extrabold">
                  {s.h1.charAt(0)}
                </div>
                <h3 className="font-display text-xl font-bold text-[#0f2847] mb-2">{s.h1.split(" in ")[0].split(" — ")[0]}</h3>
                <p className="text-sm text-slate-600 leading-relaxed flex-1">{s.description}</p>
                <span className="mt-5 inline-flex items-center text-sm font-bold text-[#0f2847] group-hover:text-[#f59321] transition-colors">
                  Learn more <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-518 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-3">
              <span className="w-6 h-[2px] bg-[#f59321]" /> Why choose 518
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0f2847] leading-[1.1]">
              The dispatch team that picks up on the <span className="text-[#f59321]">first ring.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              We are a small, local team obsessed with making the Capital Region run on time — with drivers who know your name.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/about"><Button className="btn-lift bg-[#0f2847] hover:bg-[#1e3a5f] text-white rounded-full h-12 px-6 font-semibold">Our story <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <a href="tel:5189544701"><Button variant="outline" className="border-2 border-slate-300 text-[#0f2847] hover:border-[#f59321] hover:text-[#f59321] rounded-full h-12 px-6 font-semibold bg-transparent">Call 518-954-4701</Button></a>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {WHY_US.map((f, i) => <FeatureCard key={i} icon={f.icon} title={f.title} text={f.text} />)}
          </div>
        </div>
      </section>

      {/* LOCAL SERVICE AREA */}
      <section className="py-20 md:py-24 bg-slate-50" id="service-area">
        <div className="container-518">
          <SectionHeading eyebrow="Local service area" title="Serving every corner of the" accent="Capital Region." description="Click your city to see local pickup and delivery details." />
          <div className="flex flex-wrap justify-center gap-2.5">
            {[
              {slug:"courier-service-albany-ny", city:"Albany"},
              {slug:"courier-service-schenectady-ny", city:"Schenectady"},
              {slug:"courier-service-troy-ny", city:"Troy"},
              {slug:"courier-service-saratoga-springs-ny", city:"Saratoga Springs"},
              {slug:"courier-service-clifton-park-ny", city:"Clifton Park"},
              {slug:"courier-service-latham-colonie-ny", city:"Latham & Colonie"},
              {slug:"courier-service-amsterdam-ny", city:"Amsterdam"},
              {slug:"courier-service-cohoes-ny", city:"Cohoes"},
              {slug:"courier-service-rensselaer-ny", city:"Rensselaer"},
              {slug:"courier-service-rotterdam-ny", city:"Rotterdam"}
            ].map((c) => (
              <Link key={c.slug} to={`/${c.slug}`} className="px-4 py-2 rounded-full bg-white hover:bg-[#0f2847] hover:text-white text-sm font-semibold text-slate-700 transition-colors border border-slate-200">
                <MapPin className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5 text-[#f59321]" />
                {c.city}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INTERSTATE */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-518 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-3">
              <span className="w-6 h-[2px] bg-[#f59321]" /> Interstate courier
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0f2847] leading-[1.1]">
              Beyond New York — <span className="text-[#f59321]">NY, MA, CT & VT.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              518 Courier also provides interstate courier services between New York, Massachusetts, Connecticut and Vermont for businesses and individuals — direct routes, no hubs, no waiting.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/interstate-courier-service"><Button className="btn-lift bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-12 px-6 font-semibold">Interstate details <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/get-a-quote"><Button variant="outline" className="border-2 border-[#0f2847] text-[#0f2847] hover:bg-[#0f2847] hover:text-white rounded-full h-12 px-6 font-semibold bg-transparent">Request a Quote</Button></Link>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden ring-1 ring-slate-200 shadow-xl img-zoom">
            <img src="https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzgwMjUzNDA5fDA&ixlib=rb-4.1.0&q=85" alt="Interstate courier route between New York and neighboring states" className="w-full h-[380px] object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      {/* CTA — replaces named testimonials until authorized ones are supplied */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="container-518 max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-3">
            <span className="w-6 h-[2px] bg-[#f59321]" /> Talk to us
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0f2847] leading-[1.1]">
            Need a dependable courier partner?
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Contact 518 Courier to discuss your delivery requirements. We'll walk through your route, your timing, and confirm availability by phone.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/get-a-quote">
              <Button className="btn-lift bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-13 px-6 h-14 font-semibold">
                Request a Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="tel:5189544701">
              <Button variant="outline" className="border-2 border-[#0f2847] text-[#0f2847] hover:bg-[#0f2847] hover:text-white rounded-full h-13 px-6 h-14 font-semibold bg-transparent">
                <PhoneCall className="w-4 h-4 mr-2" /> Call or text 518-954-4701
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-518 max-w-4xl">
          <SectionHeading eyebrow="FAQ" title="Frequently asked" accent="questions." />
          <div className="divide-y divide-slate-200 border-y border-slate-200">
            {[
              { q: "What areas does 518 Courier serve?", a: "We serve all of New York's Capital Region — Albany, Schenectady, Troy, Latham, Colonie, Clifton Park, Saratoga Springs, Amsterdam, Cohoes, Rensselaer, Rotterdam and neighboring towns. We also provide interstate routes into Massachusetts, Connecticut and Vermont." },
              { q: "How fast can you pick up my package?", a: "Pickup times depend on driver availability at the time of your call. Call 518-954-4701 to confirm current response times." },
              { q: "Do you handle medical deliveries?", a: "Yes. We transport medical materials such as specimens, pharmaceuticals, supplies and documents. We do not transport patients." },
              { q: "Do you offer scheduled or recurring routes?", a: "Yes. Daily, weekly and multi-stop recurring routes can be built around your business operations." },
              { q: "How do I request a quote?", a: "Use the Get a Quote form or call/text 518-954-4701. Dispatch will confirm pricing and pickup as quickly as possible." }
            ].map((f, i) => (
              <details key={i} className="group py-5 cursor-pointer">
                <summary className="flex items-center justify-between gap-4 list-none">
                  <h3 className="font-display font-bold text-[#0f2847] text-lg">{f.q}</h3>
                  <span className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-[#0f2847] group-open:bg-[#f59321] group-open:text-white group-open:border-[#f59321] transition-colors shrink-0">+</span>
                </summary>
                <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#0f2847] to-[#1e3a5f] stripe-bg text-white">
        <div className="container-518 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-3">
              <Clock className="w-3.5 h-3.5" /> Need it picked up now?
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight max-w-2xl">
              Get a driver dispatched to your door.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/get-a-quote">
              <Button className="btn-lift bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-14 px-8 text-base font-bold shadow-xl">
                Schedule a Delivery <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:5189544701">
              <Button variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0f2847] rounded-full h-14 px-8 text-base font-bold">
                <PhoneCall className="w-5 h-5 mr-2" /> Call 518-954-4701
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
